import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  IInventorySmartFloorPayloadUI,
  InventorySmartFloorsAPIKey,
} from '~/src/dash/apiV2/smartFloors/crud';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractSearchableColumnKeys,
  extractVisibleColumns,
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  spacing,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { useSmartFloorsListStore } from '~/src/dash/views/channels/SmartFloors/contexts';
import { formatSmartFloorsResponseIntoLVTRows } from '~/src/dash/views/channels/SmartFloors/SmartFloorsListView/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const queryFilteredData = (
  filteredData: IInventorySmartFloorPayloadUI[],
  query: string,
  searchableColumnKeys: string[],
): IInventorySmartFloorPayloadUI[] => {
  let queriedRows = cloneDeep(filteredData);
  if (query !== '') {
    queriedRows = filteredData.filter((row) => {
      for (const key of searchableColumnKeys) {
        if (
          row[key as keyof IInventorySmartFloorPayloadUI]
            ?.toString()
            .toLowerCase()
            .includes(query.toLowerCase())
        ) {
          return true;
        }
      }
      return false;
    });
  }
  return queriedRows;
};

export const SmartFloorsListView = (): React.ReactNode => {
  const { t } = usePublicaTranslation();
  const { user } = useBaseStore();
  const { readAuthorization, writeAuthorization, SmartFloors, handlers, shouldAllowSortingTable } =
    useSmartFloorsListStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const cfg = SmartFloors.cfg;
  const { cypressClassName, defaultState } = cfg;
  cfg.isInitialized = true;

  // states and refs
  const isMounted = useRef(false);
  const [responseData, setResponseData] = useState<IInventorySmartFloorPayloadUI[]>([]);
  const [queriedData, setQueriedData] = useState<IInventorySmartFloorPayloadUI[]>([]);
  const [prevSortedRows, setPrevSortedRows] = useState([] as IListViewTableRow[]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);

  // search for headerbidders
  const [query, setQuery] = useState('');

  // table states
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || 'Name');
  const [allowSorting, setAllowSorting] = useState<boolean>(shouldAllowSortingTable); // prop will be false if it's a change to TextInput
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  // other consts
  const searchableColumnKeys = extractSearchableColumnKeys(cfg.table.columns);

  // get the data from SmartFloorsListStore and set into state
  const getData = (): void => {
    if (readAuthorization.isAuthorized) {
      setLoadState(LoadingStateLoading);
      const smartFloors = SmartFloors.data;

      unstable_batchedUpdates(() => {
        setPrevSortedRows(cloneDeep(sortedRows)); // set previous state of sorted rows for later
        setResponseData(smartFloors);
        setLoadState(isEmpty(smartFloors) ? LoadingStateNoData : LoadingStateHasData);
        setAllowSorting(shouldAllowSortingTable);
      });
    }
  };

  useEffect(() => {
    getData();
  }, [SmartFloors.data]);

  // filter data via search query
  useEffect(() => {
    if (isMounted.current) {
      const queriedRows = queryFilteredData(responseData, query, searchableColumnKeys);
      unstable_batchedUpdates(() => {
        setQueriedData(queriedRows);
        setTotalRowCount(queriedRows.length);
        setPage(cfg.isInitialized ? defaultState.page : 0);
        setLoadState(!isEmpty(queriedRows) ? LoadingStateHasData : LoadingStateNoData);
      });
    }
  }, [responseData, query]);

  const rows = formatSmartFloorsResponseIntoLVTRows(queriedData, cfg.table);

  // sort queriedData via column or sort bys
  useEffect(() => {
    if (isMounted.current) {
      if (allowSorting) {
        if (!isEmpty(queriedData)) {
          setSortedRows(
            stableSort(rows, getComparator(order, orderBy, cfg.table.columns[orderBy].valueType)),
          );
        } else {
          setSortedRows([]);
        }
      } else {
        // keep the sort order but update what's displayed
        const updatedPrevRows = prevSortedRows;

        queriedData.forEach((newSF) => {
          prevSortedRows.forEach((row, idx) => {
            if (row.ID.value === String(newSF.SiteID)) {
              updatedPrevRows[idx].ControlBidFloor.value = Number(newSF.ControlBidFloor);
              updatedPrevRows[idx].MaxBidFloor.value = Number(newSF.MaxBidFloor);
              updatedPrevRows[idx].MinBidFloor.value = Number(newSF.MinBidFloor);
              updatedPrevRows[idx].TestIncrement.value = Number(newSF.TestIncrement);
            }
          });
        });

        setSortedRows(updatedPrevRows);
      }

      cfg.defaultState.orderBy = orderBy;
      cfg.defaultState.order = order;
    }

    // allow sorting again after a row's TextInput is done updating
    setAllowSorting(true);
  }, [queriedData, order, orderBy]);

  // update config on selected columns change
  useEffect(() => {
    if (isMounted.current) {
      cfg.defaultState.activeColumnKeys = activeColumnKeys as InventorySmartFloorsAPIKey[];
    }
  }, [activeColumnKeys]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { key, defaultOrderDirection } = column;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
    cfg.defaultState.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
    });
    cfg.defaultState.rowsPerPage = newRowsPerPage;
  };

  const handleOnChangeQuery = (text: string): void => {
    unstable_batchedUpdates(() => {
      setLoadState(LoadingStateLoading);
      setQuery(text);
    });
  };

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleSnackbarClose = (): void => {
    setSnackbarMessage('');
  };

  const handleErrorWithSnackBar = (err: string): void => {
    unstable_batchedUpdates(() => {
      setSnackbarError(true);
      setSnackbarMessage(err);
    });
  };

  // since all data is collected, we handle the pagination here
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Rows
      basePadding
      style={{
        background: publicaColors.white,
        border: `1px solid ${publicaColors.lightGray}`,
        borderRadius: borderRadius.large,
      }}
    >
      <Row noPadding paddingBottom={spacing.large}>
        <Cols
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Col>
            <TextInput
              enableClear
              handleOnChange={handleOnChangeQuery}
              msDelay={300}
              placeholder={t('SearchByNameID')}
              prefix='search'
            />
          </Col>
          <Col>
            <ColumnSelector
              // simple multi select props
              preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
              resetActiveColumnKeys={cfg.columnSelector.resetKeys}
              columnsList={extractVisibleColumns(
                cfg.table.columns,
                activeColumnKeys || [],
                isSuperUser,
              )}
              columnsHandleOnChangeOption={handleChangeActiveColumns}
              columnsHandleOnAccept={handleChangeActiveColumns}
            />
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <ListViewTable
          activeColumnKeys={activeColumnKeys}
          columns={cfg.table.columns}
          customProps={{
            selectedBidderIDForChart: SmartFloors.selectedBidderIDForSmartFloorsChart,
          }}
          customFunctions={{
            handleSelectBidderOnChart: handlers.handleSelectBidderOnChart,
            handleChangePriceText: handlers.handleUpdateBidFloorForSmartFloor,
            handleErrorWithSnackBar,
          }}
          enableActions
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          displayNameWithID
          enableCustomLeftColumn
          handleOnClickColumnHeader={handleOnClickColumnHeader}
          isReadAuthorized={readAuthorization.isAuthorized}
          isSuperUser={isSuperUser}
          isWriteAuthorized={writeAuthorization.isAuthorized}
          loadState={loadState}
          order={order}
          orderBy={orderBy}
          page={page}
          rows={visibleRows}
          rowsPerPage={rowsPerPage}
          totalRowCount={totalRowCount}
          className={cypressClassName}
        />
      </Row>

      {/* snackbar is a special case and can act as a panel */}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
    </Rows>
  );
};
